import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { THEME } from '../../../../../components-v2/Theme/theme';
import { formatCurrency } from '../../../helpers/formatCurrency';
import { styleSheet } from './styles';

interface PremiumOverTimeChartProps {
	data?: {
		title: string;
		visualizationType: string;
		data: {
			id: string;
			label: string;
			count: number;
			value: number;
		}[];
	};
	isLoading?: boolean;
	className?: string;
}

const formatDate = (date: string): string => {
	return moment(date).format('MMM D, YYYY');
};

const formatTooltipDate = (date: string): string => {
	return moment(date).format('MMMM D, YYYY');
};

const formatCurrencyAxisLabel = (value: number): string => {
	if (value >= 1000) {
		const thousands = value / 1000;
		// If it's a whole number, don't show decimals
		if (thousands % 1 === 0) {
			return `$${thousands}k`;
		}
		// Otherwise show one decimal place
		return `$${thousands.toFixed(1)}k`;
	}
	// For values less than 1000, show as whole number without cents
	return `$${Math.round(value)}`;
};

interface ChartDataPoint {
	id: string;
	label: string;
	count: number;
	value: number;
	formattedDate: string;
}

export const PremiumOverTimeChart: React.FC<PremiumOverTimeChartProps> = ({ data, isLoading, className }) => {
	const chartData = React.useMemo<ChartDataPoint[]>(() => {
		if (!data?.data || data.data.length === 0) {
			return [];
		}

		// Format dates and sort by date (data should already be sorted, but ensure it)
		return data.data
			.map(item => ({
				...item,
				formattedDate: formatDate(item.label),
			}))
			.sort((a, b) => moment(a.label).valueOf() - moment(b.label).valueOf());
	}, [data]);

	if (isLoading) {
		return (
			<div className={`${css(styleSheet.container)} ${className || ''}`}>
				<div className={css(styleSheet.loading)}>Loading chart data...</div>
			</div>
		);
	}

	if (chartData.length === 0) {
		return (
			<div className={`${css(styleSheet.container)} ${className || ''}`}>
				<div className={css(styleSheet.empty)}>No premium data available</div>
			</div>
		);
	}

	const CustomTooltip = ({ active, payload }: any) => {
		if (active && payload && payload.length) {
			const tooltipData = payload[0].payload;
			return (
				<div className={css(styleSheet.tooltip)}>
					<p className={css(styleSheet.tooltipLabel)}>{formatTooltipDate(tooltipData.label)}</p>
					<p className={css(styleSheet.tooltipValue)}>Premium: {formatCurrency(tooltipData.value)}</p>
					<p className={css(styleSheet.tooltipValue)}>Count: {tooltipData.count}</p>
				</div>
			);
		}
		return null;
	};

	return (
		<div className={`${css(styleSheet.container)} ${className || ''}`}>
			<h3 className={css(styleSheet.title)}>{data?.title || 'Renewals Premium Over Time'}</h3>
			<ResponsiveContainer width='100%' height={400}>
				<AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
					<defs>
						<linearGradient id='colorPremium' x1='0' y1='0' x2='0' y2='1'>
							<stop offset='5%' stopColor={THEME.colors.chart.levBlue} stopOpacity={0.8} />
							<stop offset='95%' stopColor={THEME.colors.chart.levBlue} stopOpacity={0} />
						</linearGradient>
					</defs>
					<CartesianGrid strokeDasharray='3 3' stroke={THEME.colors.border.main} />
					<XAxis
						dataKey='formattedDate'
						stroke={THEME.colors.text.main}
						tick={{ fontSize: 12, fill: THEME.colors.text.main }}
						angle={-45}
						textAnchor='end'
						height={80}
					/>
					<YAxis
						stroke={THEME.colors.text.main}
						tick={{ fontSize: 12, fill: THEME.colors.text.main }}
						tickFormatter={formatCurrencyAxisLabel}
					/>
					<Tooltip content={<CustomTooltip />} />
					<Area
						type='monotone'
						dataKey='value'
						stroke={THEME.colors.primary.main}
						fillOpacity={1}
						fill='url(#colorPremium)'
					/>
				</AreaChart>
			</ResponsiveContainer>
		</div>
	);
};
